import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, EmptyState } from "@/components/ui/page";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FIELDS = [
  ["first_name", "First Name"], ["last_name", "Last Name"], ["email", "Email"],
  ["phone", "Phone"], ["address", "Address"], ["source", "Source"],
  ["current_stage", "Current Stage"], ["salesperson", "Salesperson"],
  ["estimated_value", "Estimated Value"], ["notes", "Notes"],
];

export default function ImportContacts() {
  const navigate = useNavigate();
  const { selected, isAll, currentClient } = useClient();
  const [file, setFile] = useState(null);
  const [inspect, setInspect] = useState(null);
  const [mapping, setMapping] = useState({});
  const [defaultStage, setDefaultStage] = useState("LEAD");
  const [dedupe, setDedupe] = useState("email");
  const [result, setResult] = useState(null);

  const guess = (columns) => {
    const m = {};
    FIELDS.forEach(([key, label]) => {
      const found = columns.find((c) => c.toLowerCase().replace(/[^a-z]/g, "").includes(key.replace(/_/g, "").slice(0, 5)) ||
        c.toLowerCase().includes(label.toLowerCase()));
      if (found) m[key] = found;
    });
    return m;
  };

  const inspectMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData(); fd.append("file", file);
      return (await api.post(`/clients/${selected}/import/inspect`, fd)).data;
    },
    onSuccess: (d) => { setInspect(d); setMapping(guess(d.columns)); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const importMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("default_stage", defaultStage);
      fd.append("dedupe_by", dedupe);
      return (await api.post(`/clients/${selected}/import`, fd)).data;
    },
    onSuccess: (d) => { setResult(d); toast.success(`Imported ${d.imported} contacts`); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (isAll) return <div><PageHeader title="Import Contacts" /><EmptyState icon={AlertCircle} title="Select a client" subtitle="Choose a specific client to import contacts into." /></div>;

  return (
    <div className="max-w-3xl">
      <PageHeader title="Import Contacts" subtitle={`Upload a CSV or XLSX into ${currentClient?.business_name || "this client"}`} />

      {/* Step 1: upload */}
      {!inspect && !result && (
        <div className="bg-white border border-slate-200 rounded-xl p-8">
          <label className="border-2 border-dashed border-slate-300 rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition-colors" data-testid="upload-dropzone">
            <FileSpreadsheet className="h-10 w-10 text-slate-300" />
            <span className="mt-3 font-semibold text-slate-700">{file ? file.name : "Choose a CSV or XLSX file"}</span>
            <span className="text-sm text-slate-400 mt-1">Columns will be auto-detected for mapping</span>
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="file-input"
              onChange={(e) => setFile(e.target.files[0])} />
          </label>
          <Button className="mt-4" disabled={!file || inspectMut.isPending} onClick={() => inspectMut.mutate()} data-testid="inspect-btn">
            <Upload className="h-4 w-4 mr-1.5" /> {inspectMut.isPending ? "Reading…" : "Continue"}
          </Button>
        </div>
      )}

      {/* Step 2: mapping */}
      {inspect && !result && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="text-sm text-slate-500 mb-4">{inspect.row_count} rows detected · map your columns:</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FIELDS.map(([key, label]) => (
              <div key={key}>
                <Label>{label}</Label>
                <Select value={mapping[key] || "__none__"} onValueChange={(v) => setMapping((m) => ({ ...m, [key]: v === "__none__" ? undefined : v }))}>
                  <SelectTrigger className="mt-1" data-testid={`map-${key}`}><SelectValue placeholder="Not mapped" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Not mapped —</SelectItem>
                    {inspect.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
            <div>
              <Label>Default Stage (if not mapped)</Label>
              <Select value={defaultStage} onValueChange={setDefaultStage}>
                <SelectTrigger className="mt-1" data-testid="default-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{["LEAD", "PROSPECT", "RE_ENGAGED", "DEAL", "WON", "LOST"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", "-")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duplicate Matching</Label>
              <Select value={dedupe} onValueChange={setDedupe}>
                <SelectTrigger className="mt-1" data-testid="dedupe-by"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">By Email</SelectItem>
                  <SelectItem value="phone">By Phone</SelectItem>
                  <SelectItem value="both">Email or Phone</SelectItem>
                  <SelectItem value="none">Don't check</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 mt-5">
            <Button variant="outline" onClick={() => { setInspect(null); }}>Back</Button>
            <Button disabled={!mapping.first_name || importMut.isPending} onClick={() => importMut.mutate()} data-testid="run-import-btn">
              {importMut.isPending ? "Importing…" : <>Import <ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: result */}
      {result && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
          <h2 className="mt-3 text-xl font-heading font-bold text-slate-900">Import Complete</h2>
          <div className="grid grid-cols-4 gap-3 mt-6" data-testid="import-results">
            {[["Imported", result.imported, "text-emerald-600"], ["Duplicates", result.duplicates, "text-amber-600"], ["Skipped", result.skipped, "text-slate-500"], ["Errors", result.errors, "text-rose-600"]].map(([l, v, c]) => (
              <div key={l} className="rounded-lg border border-slate-200 p-3">
                <div className={`text-2xl font-heading font-bold ${c}`}>{v}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">{l}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-center mt-6">
            <Button variant="outline" onClick={() => { setFile(null); setInspect(null); setResult(null); setMapping({}); }} data-testid="import-again-btn">Import Another</Button>
            <Button onClick={() => navigate("/contacts")} data-testid="view-contacts-btn">View Contacts</Button>
          </div>
        </div>
      )}
    </div>
  );
}
